import React, { createContext, useContext, useMemo, useCallback } from 'react';
import { isRowChanged } from '../utils/rowHelpers';

const CsvEditorContext = createContext(null);

export const useCsvEditorContext = () => {
  const context = useContext(CsvEditorContext);
  if (!context) {
    throw new Error('useCsvEditorContext must be used within CsvEditorProvider');
  }
  return context;
};

export const CsvEditorProvider = ({ children, value }) => {
  const {
    rows,
    setRows,
    originalRows,
    setOriginalRows,
    originalColumnOrder,
    isLoading
  } = value;

  // Compute originalRowsMap once (memoized)
  const originalRowsMap = useMemo(() => {
    const map = new Map();
    originalRows.forEach((row) => {
      map.set(row.id, row);
    });
    return map;
  }, [originalRows]);

  // Compute rowsMap for O(1) lookups (performance optimization)
  const rowsMap = useMemo(() => {
    const map = new Map();
    rows.forEach((row) => {
      map.set(row.id, row);
    });
    return map;
  }, [rows]);

  // Compute changedRowsCount once (memoized)
  const changedRowsCount = useMemo(() => {
    let count = 0;
    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const originalRow = originalRowsMap.get(row.id);
      if (originalRow && isRowChanged(row, originalRow)) {
        count++;
      }
    }
    return count;
  }, [rows, originalRowsMap]);

  // Action: Update a single row
  const updateRow = useCallback(
    (rowId, updates) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === rowId ? { ...row, ...updates } : row
        )
      );
    },
    [setRows]
  );

  // Action: Update a single cell
  const updateCell = useCallback(
    (rowId, field, value) => {
      setRows((prevRows) =>
        prevRows.map((row) =>
          row.id === rowId ? { ...row, [field]: value } : row
        )
      );
    },
    [setRows]
  );

  // Action: Reset a row to its original state
  const resetRow = useCallback(
    (rowId) => {
      const originalRow = originalRowsMap.get(rowId);
      if (originalRow) {
        setRows((prevRows) =>
          prevRows.map((row) =>
            row.id === rowId ? { ...originalRow } : row
          )
        );
      }
    },
    [originalRowsMap, setRows]
  );

  // Action: Check if a row is changed
  const isRowChangedById = useCallback(
    (rowId) => {
      const row = rowsMap.get(rowId);
      const originalRow = originalRowsMap.get(rowId);
      if (!row || !originalRow) return false;
      return isRowChanged(row, originalRow);
    },
    [rowsMap, originalRowsMap]
  );

  const contextValue = useMemo(
    () => ({
      // Data state
      rows,
      setRows,
      originalRows,
      setOriginalRows,
      originalColumnOrder,
      isLoading,
      // Derived state
      originalRowsMap,
      rowsMap,
      changedRowsCount,
      // Actions
      updateRow,
      updateCell,
      resetRow,
      isRowChangedById
    }),
    [
      rows,
      setRows,
      originalRows,
      setOriginalRows,
      originalColumnOrder,
      isLoading,
      originalRowsMap,
      rowsMap,
      changedRowsCount,
      updateRow,
      updateCell,
      resetRow,
      isRowChangedById
    ]
  );

  return (
    <CsvEditorContext.Provider value={contextValue}>
      {children}
    </CsvEditorContext.Provider>
  );
};
